import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/app/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import { Trash2, Ban } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/");
  }, [user, loading, nav]);

  const load = async () => {
    const [s, u, p] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/projects")]);
    setStats(s.data); setUsers(u.data); setProjects(p.data);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const toggleSuspend = async (uid) => { await api.post(`/admin/users/${uid}/suspend`); toast.success("Updated"); load(); };
  const deleteProject = async (pid) => { if (confirm("Delete this project?")) { await api.delete(`/admin/projects/${pid}`); toast.success("Deleted"); load(); } };

  if (!user || user.role !== "admin") return null;

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="meta-label mb-2">Platform ops</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Admin Panel</h1>

        {stats && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {["users", "projects", "published", "likes", "comments"].map((k) => (
              <div key={k} className="rounded-lg border border-border/60 p-4 bg-card">
                <div className="meta-label">{k}</div>
                <div className="font-display font-black text-2xl mt-1">{stats[k]}</div>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="users" className="mt-10">
          <TabsList><TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger><TabsTrigger value="projects" data-testid="admin-tab-projects">Projects</TabsTrigger></TabsList>
          <TabsContent value="users" className="mt-6">
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.user_id} className="flex items-center gap-3 border border-border/60 rounded-md bg-card p-3 text-sm" data-testid={`admin-user-${u.username}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{u.full_name} <span className="text-muted-foreground">@{u.username}</span></div>
                    <div className="meta-label mt-0.5">{u.email} · {u.role}</div>
                  </div>
                  {u.is_suspended && <span className="px-2 py-0.5 text-xs bg-destructive/10 text-destructive rounded">Suspended</span>}
                  <Button variant="ghost" size="sm" onClick={() => toggleSuspend(u.user_id)} data-testid={`admin-suspend-${u.username}`}><Ban className="h-4 w-4 mr-1" />{u.is_suspended ? "Unsuspend" : "Suspend"}</Button>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="projects" className="mt-6">
            <div className="space-y-2">
              {projects.map((p) => (
                <div key={p.project_id} className="flex items-center gap-3 border border-border/60 rounded-md bg-card p-3 text-sm" data-testid={`admin-project-${p.project_id}`}>
                  <img src={p.cover_url} className="w-20 h-14 object-cover rounded" alt="" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.title}</div>
                    <div className="meta-label">by @{p.creator?.username} · {p.category}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteProject(p.project_id)} data-testid={`admin-delete-${p.project_id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </Layout>
  );
}
